import React from "react";
import { Form, Input, Button, Select, Row, Col } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Option } = Select;

// ── Styled Components ────────────────────────────────────────────────────────

const FormBody = styled.div`
  padding-bottom: 80px;

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 32px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;

    label {
      font-size: 13px;
      font-weight: 500;
      color: #333;
      .required { color: red; margin-left: 2px; }
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    height: 34px !important;
    font-size: 13px !important;
    border-radius: 3px !important;
    border: 1px solid #d9d9d9 !important;
  }

  .ant-select-selector {
    height: 34px !important;
    font-size: 13px !important;
    border-radius: 3px !important;
    border: 1px solid #d9d9d9 !important;
    display: flex !important;
    align-items: center !important;
  }

  /* FIX 1: Responsible personnel multi-select tag alignment */
  .ant-select-multiple .ant-select-selector {
    height: auto !important;
    min-height: 34px !important;
    padding: 2px 4px !important;
    align-items: flex-start !important;
    flex-wrap: wrap !important;
  }

  .ant-select-multiple .ant-select-selection-item {
    height: 24px !important;
    line-height: 22px !important;
    margin: 2px !important;
    border-radius: 4px !important;
    background: #f0fdf9 !important;
    border: 1px solid #86efca !important;
    font-size: 12px !important;
    display: inline-flex !important;
    align-items: center !important;
    padding: 0 8px !important;
  }

  .ant-select-multiple .ant-select-selection-item-remove {
    display: inline-flex !important;
    align-items: center !important;
    margin-left: 4px !important;
    font-size: 10px !important;
    color: #666 !important;
  }

  .ant-select-multiple .ant-select-selection-placeholder {
    line-height: 30px !important;
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    font-size: 13px !important;
    line-height: 32px !important;
  }

  .ant-input:focus,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused {
    border-color: #00c48c !important;
    box-shadow: 0 0 0 2px rgba(0,196,140,0.1) !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: #00c48c !important;
    box-shadow: 0 0 0 2px rgba(0,196,140,0.1) !important;
  }

  .section-title {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 20px 0 4px;
  }

  .section-sub {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 12px;
  }
`;

const SectionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
    margin-top: 28px;
    margin-bottom: 4px;
    .required { color: red; margin-left: 2px; }
`;

const SectionSub = styled.div`
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 12px;
`;

const BillingGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 32px;
`;

/*
 * FIX: Reserve a fixed amount of space for the antd Form.Item error/help
 * text under each custom-rate field (employee select + rate input).
 * Previously this text popped in/out based on live state on every
 * keystroke/selection, which changed the row's height and made the
 * whole page (and therefore the sticky footer) jump around while
 * filling in custom hourly rates on the Billing step.
 * Reserving the height means the row's total height never changes,
 * regardless of whether the help text is showing or not.
 */
const CustomRateRow = styled(Row)`
    margin-bottom: 8px;
    align-items: flex-start;

    .ant-form-item {
      margin-bottom: 0;
    }

    .ant-form-item-explain,
    .ant-form-item-explain-error {
      min-height: 18px;
      font-size: 11px;
      line-height: 18px;
    }
`;

const StyledSelect = styled(Select)`
    width: 100% !important;
`;

const StyledInput = styled(Input)`
    width: 100%;
    .ant-input{
        height: 25px !important;
    }
`;


const StyledButton = styled(Button)`
  width: auto !important;
  margin-top: ${({ $hasRates }) => ($hasRates ? "8px" : "0")};
  display:flex;
  align-items:center;
  background: #deefee !important;
  border-radius: 5px;
  color: #00685F !important;
  border: 1px solid #deefee  !important;
  width: 100px;
   &:hover {
      color: #00685F !important;
  }
   &:focus {
      color: #00685F !important;
  }
`;

const CustomRateFormItem = styled(Form.Item)`
    margin-bottom: 0;
    .ant-input{
        height: 25px !important;
    }
`;

// ── Component ────────────────────────────────────────────────────────────────

const BillingForMatter = ({
    form,
    roleList = [],
    locationList = [],
    selectedClientData,
    billingMethod,
    onBillingMethodChange,
    userList = [],
    customRates = [],
    setCustomRates,
    resourceList = [],
}) => {

    const matterUserIds = new Set(
        (resourceList || []).filter((r) => {
            const start = r.actualBookedStartDate ?? r.raw?.actualBookedStartDate ?? r.startDate ?? r.raw?.startDate;
            const end = r.actualBookedEndDate ?? r.raw?.actualBookedEndDate ?? r.finishDate ?? r.raw?.finishDate;
            return start != null || end != null;
        }).flatMap((r) => {
            const ids = [
                r.userId,
                r.userid,
                r.raw?.userId,
                r.raw?.userid,
                r.employeeId,
                r.employeeid,
                r.raw?.employeeId,
            ].filter(Boolean);
            return ids;
        })
    );

    const matterUserOptions = (userList || [])
        .filter((u) => matterUserIds.has(u.userid) || matterUserIds.has(u.userId))
        .map((u) => ({
            label: u.fullname,
            value: u.userid,
        }));

    React.useEffect(() => {
        if (!customRates?.length) return;
        const validUserIds = new Set(matterUserOptions.map((u) => u.value));
        const filtered = customRates.filter((r) => !r.userId || validUserIds.has(r.userId));
        if (filtered.length !== customRates.length) {
            setCustomRates(filtered);
        }
    }, [matterUserOptions.map((u) => u.value).join(",")]);

    const allMatterUsersHaveRates =
        matterUserOptions?.length > 0 &&
        customRates?.length >= matterUserOptions?.length &&
        matterUserOptions?.every((u) =>
            customRates?.some((r) => r?.userId === u?.value && r?.rate)
        );

    const handleAddCustomRate = () => {
        if (allMatterUsersHaveRates) return;
        setCustomRates([...customRates, { userId: "", rate: "" }]);
    };

    const handleRemoveCustomRate = (index) => {
        const updated = [...customRates];
        updated.splice(index, 1);
        setCustomRates(updated);
    };

    const handleCustomRateChange = (index, field, value) => {
        const updated = [...customRates];
        updated[index][field] = value;
        setCustomRates(updated);
    };

    const isCompany = selectedClientData?.clienttype === "company";
    const responsiblePersonnelOptions = selectedClientData?.responsiblepersonnel || [];

    return (
        <FormBody>
            <Form form={form} layout="vertical">

                {isCompany && (
                    <>
                        <SectionTitle><span className="required">*</span>Responsible Personnel for this Matter</SectionTitle>
                        <SectionSub>
                            Choose at least one person who will be responsible &amp; primary contact for this matter
                        </SectionSub>
                        {/* FIX 1: Wrap in a div with max-width so tags align properly */}
                        <div style={{ maxWidth: 500 }}>
                            <Form.Item
                                name="responsiblePersonnel"
                                rules={[{ required: true, message: "Please select at least one responsible person" }]}
                                style={{ marginBottom: 16 }}
                            >
                                <StyledSelect
                                    mode="multiple"
                                    placeholder="Select responsible personnel"
                                    style={{ width: "100%" }}
                                    // Ensure tags wrap correctly
                                    maxTagCount={undefined}
                                >
                                    {responsiblePersonnelOptions.map((person) => (
                                        <Option key={person.email} value={person.email}>
                                            {person.name}{person.designation ? ` (${person.designation})` : ""}
                                        </Option>
                                    ))}
                                </StyledSelect>
                            </Form.Item>
                        </div>
                    </>
                )}

                <SectionTitle>Billing Preference</SectionTitle>

                <BillingGrid>
                    {/* Left: Billing Method */}
                    <Form.Item
                        name="billingMethod"
                        label="Billing method"
                        rules={[{ required: true, message: "Billing method is required" }]}
                    >
                        <StyledSelect
                            placeholder="Select billing method"
                            onChange={(val) => {
                                onBillingMethodChange(val);

                                // ❗ Only reset if user manually changes (not initial load)
                                if (form.isFieldsTouched()) {
                                    form.setFieldsValue({
                                        billingFlatFee: undefined,
                                        billingContingencyFee: undefined,
                                    });
                                    setCustomRates([]);
                                }
                            }}
                        >
                            <Option value="Hourly">Hourly</Option>
                            <Option value="Flat Fee">Flat Fee</Option>
                            <Option value="Contingency Fee">Contingency Fee</Option>
                        </StyledSelect>
                    </Form.Item>

                    {/* FIX 2 & 3: Only show matter users in hourly dropdown, disable button when all have rates */}
                    {billingMethod === "Hourly" && (
                        <CustomRateFormItem
                            label="Custom Hourly Rate"
                            style={{
                                gridColumn: customRates?.length === 0 ? "2 / 3" : "2 / 3"
                            }}
                        >
                            {customRates?.map((item, index) => (
                                <CustomRateRow gutter={12} key={index}>
                                    <Col span={10}>
                                        <Form.Item
                                            validateStatus={!item.userId ? "error" : ""}
                                            help={!item.userId ? "Please choose employee" : ""}
                                        >
                                            <StyledSelect
                                                value={item.userId || undefined}
                                                placeholder="Choose the employee"
                                                onChange={(val) =>
                                                    handleCustomRateChange(index, "userId", val)
                                                }
                                            >
                                                {matterUserOptions.map((u) => (
                                                    <Option
                                                        key={u.value}
                                                        value={u.value}
                                                        disabled={customRates.some(
                                                            (r, i) => i !== index && r.userId === u.value
                                                        )}
                                                    >
                                                        {u.label}
                                                    </Option>
                                                ))}
                                            </StyledSelect>
                                        </Form.Item>
                                    </Col>

                                    <Col span={10}>
                                        <Form.Item
                                            validateStatus={
                                                !item.rate || isNaN(item.rate) || Number(item.rate) <= 0
                                                    ? "error"
                                                    : ""
                                            }
                                            help={
                                                !item.rate || isNaN(item.rate) || Number(item.rate) <= 0
                                                    ? "Enter valid hourly rate"
                                                    : ""
                                            }
                                        >
                                            <StyledInput
                                                value={item.rate}
                                                prefix="$"
                                                placeholder="Hourly rate (Eg: 200)"
                                                onChange={(e) =>
                                                    handleCustomRateChange(index, "rate", e.target.value)
                                                }
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col span={4}>
                                        <Button
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleRemoveCustomRate(index)}
                                        />
                                    </Col>
                                </CustomRateRow>
                            ))}
                            {/* FIX 3: Disable button when all matter users have been assigned custom rates */}
                            <StyledButton
                                $hasRates={customRates?.length > 0}
                                // className="custom-rate-btn"
                                icon={<PlusOutlined />}
                                onClick={handleAddCustomRate}
                                disabled={allMatterUsersHaveRates || matterUserOptions?.length === 0}
                                block={customRates?.length === 0}
                                title={
                                    allMatterUsersHaveRates
                                        ? "All matter resources have been assigned custom rates"
                                        : matterUserOptions?.length === 0
                                            ? "No resources added to this matter yet"
                                            : ""
                                }
                            >
                                Add Custom Rate
                            </StyledButton>
                            {allMatterUsersHaveRates && (
                                <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                                    {matterUserOptions?.length === 1
                                        ? "1 resource has been assigned a custom rate."
                                        : `All ${matterUserOptions?.length} resources have been assigned custom rates.`
                                    }
                                </div>
                            )}
                        </CustomRateFormItem>
                    )}

                    {/* Right: Flat Fee */}
                    {(billingMethod === "Flat Fee" || form.getFieldValue("billingMethod") === "Flat Fee") && (
                        <Form.Item
                            name="billingFlatFee"
                            label="Flat Fee Detail"
                            rules={[{ required: true, message: "Flat fee amount is required" }]}
                        >
                            <StyledInput prefix="$" placeholder="Amount (Eg: 200)" />
                        </Form.Item>
                    )}

                    {/* Right: Contingency Fee */}
                    {billingMethod === "Contingency Fee" && (
                        <Form.Item
                            name="billingContingencyFee"
                            label="Contingency Fee Detail"
                            rules={[
                                { required: true, message: "Contingency fee is required" },
                                {
                                    validator: (_, value) =>
                                        !value || isNaN(value) || Number(value) <= 0 || Number(value) > 100
                                            ? Promise.reject("Enter a valid percentage (1–100)")
                                            : Promise.resolve(),
                                },
                            ]}
                        >
                            <StyledInput prefix="%" placeholder="Rate (Eg: 20%)" />
                        </Form.Item>
                    )}

                    {!billingMethod && <div />}
                </BillingGrid>

            </Form>
        </FormBody>
    );
};

export default BillingForMatter;